import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ButtonNB from '../ui-nb/ButtonNB';
import CardNB from '../ui-nb/CardNB';
import IconNB from '../ui-nb/IconNB';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../ToastNB';

interface Expense {
  id: string;
  order_id: string;
  description: string;
  amount: number;
  created_by: string;
  created_at: string;
  profiles?: { id: string; full_name: string } | null;
}

interface Props {
  orderId: string;
}

export default function OrderExpenseList({ orderId }: Props) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const user = useAuthStore(s => s.user);
  const { addToast } = useToast();

  const fetchExpenses = async () => {
    const { data } = await supabase
      .from('order_expenses')
      .select('*, profiles:created_by(id, full_name)')
      .eq('order_id', orderId)
      .order('created_at', { ascending: false });

    if (data) setExpenses(data as Expense[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchExpenses();
  }, [orderId]);

  const handleSubmit = async () => {
    if (!description.trim() || !amount || !user?.id) return;
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount < 0) return;

    setSubmitting(true);

    const { error } = await supabase.from('order_expenses').insert({
      order_id: orderId,
      description: description.trim(),
      amount: parsedAmount,
      created_by: user.id,
    });

    setSubmitting(false);

    if (error) {
      addToast('Failed to add expense', 'error');
      return;
    }

    setDescription('');
    setAmount('');
    setShowForm(false);
    fetchExpenses();
    addToast('Expense added', 'success');
  };

  const total = expenses.reduce((sum, e) => sum + (typeof e.amount === 'number' ? e.amount : 0), 0);

  return (
    <CardNB className="p-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase">
          <span className="inline-flex items-center gap-2">
            <IconNB name="currency" size={16} />
            Extra expenses
          </span>
        </h2>
        <ButtonNB
          variant="secondary"
          size="sm"
          onClick={() => setShowForm(!showForm)}
        >
          <IconNB name={showForm ? 'minus' : 'plus'} size={14} />
          {showForm ? 'Cancel' : 'Add'}
        </ButtonNB>
      </div>

      {showForm && (
        <div className="mb-4 p-3 border-2 border-nb-black bg-nb-cream">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
            <div className="sm:col-span-2">
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Description (e.g. replacement screen)"
                className="w-full px-3 py-2 bg-white border-2 border-nb-black text-sm focus:outline-none focus:border-nb-yellow rounded-[2px]"
              />
            </div>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-nb-text-muted text-sm font-medium">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-6 pr-2 py-2 bg-white border-2 border-nb-black text-sm focus:outline-none focus:border-nb-yellow rounded-[2px]"
              />
            </div>
          </div>
          <ButtonNB
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!description.trim() || !amount}
          >
            Save expense
          </ButtonNB>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-nb-text-muted">Loading expenses...</p>
      ) : expenses.length === 0 ? (
        <p className="text-sm text-nb-text-muted">No extra expenses registered</p>
      ) : (
        <>
          <div className="space-y-2">
            {expenses.map(expense => (
              <div key={expense.id} className="flex items-center justify-between py-1.5 border-b border-nb-black/10 last:border-b-0">
                <span className="text-sm text-nb-black">{expense.description}</span>
                <span className="text-sm font-nb font-bold text-nb-black">
                  ${typeof expense.amount === 'number' ? expense.amount.toFixed(2) : expense.amount}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t-2 border-nb-black flex justify-between">
            <span className="text-sm font-nb font-bold text-nb-black">Total</span>
            <span className="text-sm font-nb font-bold text-nb-black">${total.toFixed(2)}</span>
          </div>
        </>
      )}
    </CardNB>
  );
}
